import { Injectable } from '@angular/core';
import { PowerUnit } from '../../constants/power_unit_enum';

enum ScreenSizes {
  sm = 640,
  md = 768,
  lg = 1024,
  xl = 1280,
}
@Injectable({
  providedIn: 'root',
})
export class Functions {
  /**
   * Operation to get back if an input/output is on or off
   * @param ioNumber Bit-number that is saved in the DB. Like 832
   * @param placeNo Place you want to know it from (output/input place. Like 1 - 10)
   * @returns boolean
   */
  bitwiseIO(ioNumber: number, placeNo: number): boolean {
    placeNo = placeNo - 1;
    return (ioNumber & (1 << placeNo)) != 0;
  }

  /**
   * Function to check if the screensize is at some breakpoint
   * @param screenSize ScreenSize. Size to compare with
   * @returns boolean
   */
  isScreenWidth(screenSize: ScreenSizes): boolean {
    return window.innerWidth >= screenSize;
  }

  /**
   * Check for a panel; if it should be collapsed (Should be collapsed if a screen is smaller than lg size)
   * @returns boolean
   */
  shouldBeCollapsed(): boolean {
    return !this.isScreenWidth(ScreenSizes.lg);
  }

  /**
   * This function will convert to the largest option possible and will give back the new power and powerunit
   * @param power Power that should be converted (in W!)
   * @returns object with new power and powerunit
   */
  getConvertedPower(power: number): {
    power: number;
    powerUnit: string;
    isExport: boolean;
  } {
    const isExport = power < 0;
    let powerUnit = PowerUnit.W;
    if (power === 0) {
      // 0 watts
      return {
        power: 0,
        powerUnit: this.getPowerUnitAsString(powerUnit),
        isExport,
      };
    }

    if (power > 1000000 || power < -1000000) {
      power = Math.round((power / 1000000 + Number.EPSILON) * 10) / 10;
      powerUnit = PowerUnit.MW;
    } else if (power > 1000 || power < -1000) {
      power = Math.round((power / 1000 + Number.EPSILON) * 10) / 10;
      powerUnit = PowerUnit.kW;
    } else {
      powerUnit = PowerUnit.W;
    }

    // power = Math.round((power + Number.EPSILON) * 10) / 10;
    power = power < 0 ? Math.abs(power) : power;

    return {
      power: power,
      powerUnit: this.getPowerUnitAsString(powerUnit),
      isExport: isExport,
    };
  }

  getPowerUnitAsString(powerUnit: PowerUnit): string {
    switch (powerUnit) {
      case PowerUnit.MW:
        return 'MW';
      case PowerUnit.kW:
        return 'kW';
      default:
        return 'W';
    }
  }

  getDeviceStatus(available: boolean, error: number, active?: boolean): string {
    if (active !== undefined && active === false) {
      return 'OFF';
    }
    if (available) {
      if (error === 0) {
        return 'ONLINE';
      }
      return 'TIMEOUT';
    } else {
      return 'NOT USED';
    }
  }

  getShortenedMainFW(mainFW: string): string {
    const splittetVersion = mainFW.split('+');
    return splittetVersion.length > 0 ? splittetVersion[0] : mainFW;
  }

  copyText(value: string) {
    navigator.clipboard.writeText(value);
  }

  generateTimestampsForWholeDay(date: string): { x: string; y: number }[] {
    const result: { x: string; y: number }[] = [];

    // Start at midnight
    const start = new Date(`${date}T00:00:00`);
    const end = new Date(`${date}T23:59:00`);

    const current = new Date(start);

    while (current <= end) {
      result.push({ x: current.toISOString(), y: NaN });
      current.setMinutes(current.getMinutes() + 1);
    }

    return result;
  }

  generateTimestampsForLastHour(): { x: string; y: number }[] {
    const result: { x: string; y: number }[] = [];
    const now = new Date(new Date().setSeconds(0, 0));
    const start = new Date(now).setMinutes(now.getMinutes() - 59);
    const end = now;

    const current = new Date(start);

    while (current <= end) {
      result.push({ x: current.toISOString(), y: NaN });
      current.setMinutes(current.getMinutes() + 1);
    }

    return result;
  }

  insertInArrayByDeviceNo(array: any, newItem: any) {
    if (array.length === 0) {
      array.push(newItem);
      return;
    }

    if (newItem.deviceNo) {
      const ids = array.map((item: any) => item.deviceNo);
      const minId = Math.min(...ids);
      const maxId = Math.max(...ids);

      if (newItem.deviceNo < minId) {
        array.unshift(newItem);
      } else if (newItem.deviceNo > maxId) {
        array.push(newItem);
      } else {
        const index = array.findIndex(
          (item: any) => item.deviceNo > newItem.deviceNo,
        );
        array.splice(index, 0, newItem);
      }
    } else {
      const ids = array.map((item: any) => item.ID);
      const minId = Math.min(...ids);
      const maxId = Math.max(...ids);

      if (newItem.ID < minId) {
        array.unshift(newItem);
      } else if (newItem.ID > maxId) {
        array.push(newItem);
      } else {
        const index = array.findIndex((item: any) => item.ID > newItem.ID);
        array.splice(index, 0, newItem);
      }
    }
  }
}
